import { useState, type FormEvent } from 'react';
import { Megaphone, Plus, Pencil, Trash2, Loader2, Pin, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  usePlatformAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  type AdminAnnouncement,
  type AnnouncementInput,
} from '@/hooks/usePlatform';
import { useConfirm } from '@/components/ConfirmDialog';
import { ApiError } from '@/lib/api-client';
import { formatDate, cn } from '@/lib/utils';

const SEVERITY_STYLES: Record<string, string> = {
  info: 'border-blue-500/30 bg-blue-500/5',
  success: 'border-emerald-500/30 bg-emerald-500/5',
  warning: 'border-amber-500/30 bg-amber-500/5',
  danger: 'border-destructive/30 bg-destructive/5',
};

export function PlatformAnnouncementsPage() {
  const { data, isPending } = usePlatformAnnouncements();
  const [editing, setEditing] = useState<AdminAnnouncement | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Megaphone className="w-5 h-5" /> Duyurular
          </h1>
          <p className="text-muted-foreground text-sm">
            Dashboard sağ panelinde tüm kullanıcılara, sadece adminlere veya sadece owner'a gösterilir.
          </p>
        </div>
        {!creating && !editing && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4" /> Yeni Duyuru
          </Button>
        )}
      </div>

      {(creating || editing) && (
        <AnnouncementForm
          initial={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      )}

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : (data?.announcements.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <Megaphone className="w-6 h-6 mx-auto mb-2 opacity-50" />
            Henüz duyuru yok.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data!.announcements.map((a) => (
            <AnnouncementRow key={a.id} announcement={a} onEdit={() => setEditing(a)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AnnouncementRow({
  announcement,
  onEdit,
}: {
  announcement: AdminAnnouncement;
  onEdit: () => void;
}) {
  const del = useDeleteAnnouncement();
  const update = useUpdateAnnouncement();
  const confirm = useConfirm();
  const now = Math.floor(Date.now() / 1000);
  const expired = !!announcement.expires_at && announcement.expires_at < now;

  const onDelete = async () => {
    const ok = await confirm({
      title: `"${announcement.title}" sil?`,
      description: 'Kalıcı olarak silinir. Kullanıcıların dismiss kayıtları da gider.',
      danger: true,
    });
    if (!ok) return;
    await del.mutateAsync(announcement.id);
  };

  const togglePinned = async () => {
    await update.mutateAsync({
      id: announcement.id,
      body: { pinned: !announcement.pinned },
    });
  };

  return (
    <Card className={cn(SEVERITY_STYLES[announcement.severity] ?? '')}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold">{announcement.title}</h3>
              <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-[10px] px-1.5 py-0.5 font-mono">
                {announcement.id}
              </span>
              <span className="inline-flex items-center rounded-full bg-background border text-[10px] px-1.5 py-0.5">
                {announcement.severity}
              </span>
              <span className="inline-flex items-center rounded-full bg-background border text-[10px] px-1.5 py-0.5">
                {announcement.target_audience}
              </span>
              {announcement.pinned ? (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5">
                  <Pin className="w-2.5 h-2.5" /> sabit
                </span>
              ) : null}
              {expired && (
                <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-[10px] px-1.5 py-0.5">
                  süresi dolmuş
                </span>
              )}
            </div>
            {announcement.body_html && (
              <div
                className="text-sm text-muted-foreground prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: announcement.body_html }}
              />
            )}
            <div className="text-xs text-muted-foreground">
              Yayın: {formatDate(announcement.published_at)}
              {announcement.expires_at && (
                <> · Bitiş: {formatDate(announcement.expires_at)}</>
              )}
              {announcement.link_url && (
                <>
                  {' '}
                  · <a href={announcement.link_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    {announcement.link_label || announcement.link_url}
                  </a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" variant="ghost" onClick={togglePinned} title="Sabitle">
              <Pin className={cn('w-4 h-4', announcement.pinned && 'text-primary fill-primary')} />
            </Button>
            <Button size="sm" variant="ghost" onClick={onEdit}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete} disabled={del.isPending}>
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AnnouncementForm({
  initial,
  onClose,
}: {
  initial: AdminAnnouncement | null;
  onClose: () => void;
}) {
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();
  const isEdit = !!initial;
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<AnnouncementInput>({
    id: initial?.id ?? '',
    title: initial?.title ?? '',
    body_html: initial?.body_html ?? '',
    severity: initial?.severity ?? 'info',
    link_url: initial?.link_url ?? '',
    link_label: initial?.link_label ?? '',
    target_audience: initial?.target_audience ?? 'all',
    pinned: !!initial?.pinned,
    dismissible: initial ? !!initial.dismissible : true,
    expires_at: initial?.expires_at ?? null,
  });

  const expiresISO = form.expires_at
    ? new Date(form.expires_at * 1000).toISOString().slice(0, 16)
    : '';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEdit) {
        const { id: _ignore, ...body } = form;
        void _ignore;
        await update.mutateAsync({ id: initial!.id, body });
      } else {
        await create.mutateAsync(form);
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            {isEdit ? `Düzenle: ${initial!.id}` : 'Yeni Duyuru'}
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <CardDescription>
          HTML'i sadece sen girersin, sanitize edilmez — güvenli tutmak sana kalmış.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="a-id">ID (url-slug)</Label>
              <Input
                id="a-id"
                required
                disabled={isEdit}
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                placeholder="ornek: v0-2-release"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="a-severity">Önem</Label>
              <select
                id="a-severity"
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value as AnnouncementInput['severity'] })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="info">Info (mavi)</option>
                <option value="success">Success (yeşil)</option>
                <option value="warning">Warning (sarı)</option>
                <option value="danger">Danger (kırmızı)</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="a-title">Başlık</Label>
            <Input
              id="a-title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Sürüm 0.2.0 — AI içerik üretimi yayında"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="a-body">Gövde (HTML)</Label>
            <textarea
              id="a-body"
              rows={5}
              value={form.body_html}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              placeholder="<p>Yeni özellikler: ...</p>"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="a-link">Link URL</Label>
              <Input
                id="a-link"
                type="url"
                value={form.link_url}
                onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="a-link-label">Link Yazısı</Label>
              <Input
                id="a-link-label"
                value={form.link_label}
                onChange={(e) => setForm({ ...form, link_label: e.target.value })}
                placeholder="Detaylara git →"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="a-audience">Hedef Kitle</Label>
              <select
                id="a-audience"
                value={form.target_audience}
                onChange={(e) => setForm({ ...form, target_audience: e.target.value as AnnouncementInput['target_audience'] })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">Herkes</option>
                <option value="user">Sadece kullanıcılar (owner hariç)</option>
                <option value="owner">Sadece owner</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="a-expires">Bitiş (opsiyonel)</Label>
              <Input
                id="a-expires"
                type="datetime-local"
                value={expiresISO}
                onChange={(e) =>
                  setForm({
                    ...form,
                    expires_at: e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null,
                  })
                }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.pinned}
                onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                className="h-4 w-4 rounded border-input"
              />
              Sabitle (en üstte göster)
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.dismissible}
                onChange={(e) => setForm({ ...form, dismissible: e.target.checked })}
                className="h-4 w-4 rounded border-input"
              />
              Kullanıcı kapatabilsin
            </label>
          </div>

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 rounded px-2 py-1.5">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t">
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {(create.isPending || update.isPending) ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isEdit ? 'Güncelle' : 'Yayınla'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              İptal
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
